import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { clientPath } from '../lib/clientLink.js';

// Session 14: agency-only traffic alerts with per-user read state
// (portal_alert_reads, server-side). Unread alerts are listed with
// "Mark as read"; read ones fold into one "N earlier alerts (read)" line
// that expands to dimmed rows. Resolved alerts never reach the views.

// v_portal_attention alert rows carry job_id 'analytics-alert-<id>'.
export const alertIdOf = (row) => Number(String(row.job_id).replace('analytics-alert-', ''));

export const markAlertRead   = (id) => supabase.rpc('portal_mark_alert_read',   { p_alert_id: id });
export const markAlertUnread = (id) => supabase.rpc('portal_mark_alert_unread', { p_alert_id: id });
export const markAlertsRead  = (clientId) => supabase.rpc('portal_mark_alerts_read', { p_client_id: clientId ?? null });

function fmtDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function AlertCard({ row, read, clientLabel, onRead, onUnread, busy }) {
  return (
    <div className={`attention-card ${read ? 'attention-card-waiting' : ''}`}>
      <div className="attention-body">
        <span className="attention-kind">Alert</span>
        {clientLabel && <span className="attention-client">{clientLabel}</span>}
        <div className="attention-title">{row.label}</div>
        <div className="attention-sub">{row.title}{row.sort_at ? ` · ${fmtDay(row.sort_at)}` : ''}</div>
      </div>
      <div className="alert-actions">
        {read
          ? <button type="button" className="link-btn" disabled={busy} onClick={onUnread}>Mark as unread</button>
          : <button type="button" className="link-btn" disabled={busy} onClick={onRead}>Mark as read</button>}
        <Link to={clientPath(row.route, row.client_id)} className="attention-cta">Open →</Link>
      </div>
    </div>
  );
}

// One client's (or one flat) list: unread cards, then the folded read line.
export function AlertRows({ rows, clientName, showClient, onChange }) {
  const [showRead, setShowRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const unread = rows.filter((r) => !r.is_read);
  const read   = rows.filter((r) => r.is_read);

  const run = async (fn, ids, isRead) => {
    setBusy(true); setErr(null);
    const { error } = await fn();
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onChange(ids, isRead);
  };

  return (
    <div className="attention-list">
      {err && <p className="hint">Couldn't update the alert: {err}</p>}
      {unread.map((r) => (
        <AlertCard key={r.job_id} row={r} busy={busy} clientLabel={showClient ? clientName(r.client_id) : null}
                   onRead={() => run(() => markAlertRead(alertIdOf(r)), [r.job_id], true)} />
      ))}
      {read.length > 0 && (
        <button type="button" className="alert-earlier" aria-expanded={showRead} onClick={() => setShowRead((v) => !v)}>
          {showRead ? '▾' : '▸'} {read.length} earlier alert{read.length === 1 ? '' : 's'} (read)
        </button>
      )}
      {showRead && read.map((r) => (
        <AlertCard key={r.job_id} row={r} read busy={busy} clientLabel={showClient ? clientName(r.client_id) : null}
                   onUnread={() => run(() => markAlertUnread(alertIdOf(r)), [r.job_id], false)} />
      ))}
    </div>
  );
}

// "All clients": one collapsible group per client, header = name + unread
// count + Mark all as read (for that client only).
export function AlertGroups({ rows, clientName, onChange }) {
  const [closed, setClosed] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const byClient = new Map();
  rows.forEach((r) => { if (!byClient.has(r.client_id)) byClient.set(r.client_id, []); byClient.get(r.client_id).push(r); });
  const groups = [...byClient.entries()].sort((a, b) => clientName(a[0]).localeCompare(clientName(b[0])));

  const markGroup = async (clientId, groupRows) => {
    setBusy(clientId); setErr(null);
    const { error } = await markAlertsRead(clientId);
    setBusy(null);
    if (error) { setErr(error.message); return; }
    onChange(groupRows.filter((r) => !r.is_read).map((r) => r.job_id), true);
  };

  return (
    <>
      {err && <p className="hint">Couldn't update the alerts: {err}</p>}
      {groups.map(([clientId, groupRows]) => {
        const unreadN = groupRows.filter((r) => !r.is_read).length;
        const isOpen = !closed[clientId];
        return (
          <div key={clientId} className="alert-group">
            <div className="alert-group-head">
              <button type="button" className="alert-group-toggle" aria-expanded={isOpen}
                      onClick={() => setClosed((s) => ({ ...s, [clientId]: isOpen }))}>
                <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span> {clientName(clientId)}
                <span className="count">{unreadN} unread</span>
              </button>
              {unreadN > 0 && (
                <button type="button" className="link-btn" disabled={busy === clientId}
                        onClick={() => markGroup(clientId, groupRows)}>Mark all as read</button>
              )}
            </div>
            {isOpen && <AlertRows rows={groupRows} clientName={clientName} onChange={onChange} />}
          </div>
        );
      })}
    </>
  );
}
